import React, { useMemo } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeftIcon, ArrowTopRightOnSquareIcon, CheckIcon } from '@heroicons/react/24/outline'
import { swryaBanner, swryaModules } from '../../Data/swryaPromo'
import { proVerticals } from '../../Data/proVerticals'
import { useProSelection } from '../../services/products'
import ImagePlaceholder from '../Elements/ImagePlaceholder'
import swryaLogo from '../../Media/swrya-logo.png'

const levelStyle = {
  1: { badge: 'bg-gray-100 text-gray-600', accent: 'border-l-gray-400' },
  2: { badge: 'bg-electric-50 text-electric-700', accent: 'border-l-electric-500' },
  3: { badge: 'bg-red-50 text-red-700', accent: 'border-l-red-700' },
}

export default function ProductDetailPage() {
  const { id } = useParams()
  const { t } = useTranslation('catalogo')
  const { products, loading, error } = useProSelection()
  const promo = useMemo(() => swryaModules[Math.floor(Math.random() * swryaModules.length)], [id])
  const producto = products.find((p) => p.id === id)

  if (loading) return <p className="bg-white px-6 pb-32 pt-40 text-center text-sm text-gray-500">{t('pro.status.loading')}</p>
  if (error) return <p className="bg-white px-6 pb-32 pt-40 text-center text-sm text-red-700">{t('pro.status.error')}</p>
  if (!producto) return <Navigate to="/catalogo" replace />

  const imagen = producto.specs?.find(([key]) => key === 'Image')?.[1]
  const verticalId = proVerticals.find((v) => v.match.test(producto.operacion))?.id
  // El servidor no define recomendados: se sugieren los de la misma operación y luego el resto de Pro Selection.
  const otros = products.filter((p) => p.id !== producto.id)
  const recomendados = [...otros.filter((p) => p.operacion === producto.operacion), ...otros.filter((p) => p.operacion !== producto.operacion)].slice(0, 6)

  return (
    <div className="bg-white">
      {/* HERO */}
      <header className="relative isolate overflow-hidden bg-gradient-to-b from-electric-50 to-white px-6 pb-12 pt-28 lg:px-8">
        <div data-no-float className="pointer-events-none absolute -right-24 -top-24 -z-10 h-80 w-80 rounded-full bg-electric-100 opacity-70 blur-3xl" aria-hidden="true" />
        <div className="mx-auto max-w-6xl">
          <nav className="flex flex-wrap items-center gap-2 text-sm text-gray-500" aria-label="breadcrumb">
            <Link to="/catalogo" className="inline-flex items-center gap-1.5 font-semibold text-navy-900 transition hover:text-electric-600">
              <ArrowLeftIcon className="h-4 w-4" /> {t('detail.back')}
            </Link>
            <span aria-hidden="true">/</span>
            <span className="font-semibold text-gray-700">{producto.nombre}</span>
          </nav>

          <div className="mt-8 grid items-center gap-10 rounded-3xl border border-gray-200 bg-white p-6 shadow-sm sm:p-10 md:grid-cols-5">
            <div className="flex h-64 items-center justify-center rounded-2xl bg-electric-50/50 p-6 md:col-span-2 md:h-72">
              {imagen ? (
                <img src={imagen} alt={producto.nombre} className="max-h-full max-w-full object-contain" />
              ) : (
                <ImagePlaceholder className="h-full w-full rounded-xl" />
              )}
            </div>
            <div className="md:col-span-3">
              <div className="flex flex-wrap items-center gap-2">
                {(producto.categoria || producto.familia) && (
                  <span className="rounded-full bg-electric-50 px-3 py-1 text-xs font-bold text-electric-600">{producto.categoria || producto.familia}</span>
                )}
                {producto.swrya && (
                  <span className="inline-flex items-center gap-2 rounded-full bg-orange-50 px-3 py-1">
                    <img src={swryaLogo} alt="SWRYA" className="h-3.5 w-auto" />
                    <span className="text-xs font-bold text-orange-700">{t('pro.card.swrya')}</span>
                  </span>
                )}
              </div>
              {producto.marca && <p className="mt-5 text-sm font-bold uppercase tracking-[0.2em] text-gray-500">{producto.marca}</p>}
              <h1 className="mt-1 text-4xl font-extrabold tracking-tight text-navy-900 sm:text-5xl">{producto.nombre}</h1>
              <p className="mt-2 text-sm text-gray-500">{producto.codigo}</p>
              {producto.descripcion && <p className="mt-4 max-w-xl text-sm leading-relaxed text-gray-600">{producto.descripcion}</p>}
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link to="/contact" state="cotizar-equipo" className="rounded-md bg-navy-900 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-electric-600 sm:text-base">
                  {t('pro.card.quote')} <span aria-hidden="true">&rarr;</span>
                </Link>
                {producto.specs.length > 0 && (
                  <a href="#especificaciones" className="text-sm font-bold text-electric-600 transition hover:text-navy-900">
                    {t('detail.specs')} <span aria-hidden="true">&darr;</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* CONTENIDO */}
      <div className="mx-auto max-w-6xl px-6 pb-16 pt-8 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-3">
          <div className="space-y-12 lg:col-span-2">
            {producto.specs.length > 0 && (
            <section id="especificaciones" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-navy-900">{t('detail.specs')}</h2>
              <div className="mt-2 h-1 w-12 rounded-full bg-neon-300" />
              <dl className="mt-6 grid gap-4 sm:grid-cols-2">
                {producto.specs.map(([k, v]) => (
                  <div key={k} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                    <dt className="text-xs font-bold uppercase tracking-wide text-electric-600">{k}</dt>
                    <dd className="mt-1.5 font-medium leading-snug text-navy-900">{v}</dd>
                  </div>
                ))}
              </dl>
            </section>
            )}

            <section>
              <h2 className="text-2xl font-bold text-navy-900">{t('detail.accessories')}</h2>
              <div className="mt-2 h-1 w-12 rounded-full bg-neon-300" />
              {producto.accesorios.length > 0 ? (
                <>
                  <p className="mt-5 text-xs font-bold uppercase tracking-wide text-gray-500">{t('pro.card.kitIncludes')}</p>
                  <ul className="mt-3 flex flex-wrap gap-2">
                    {producto.accesorios.map((a, i) => (
                      <li key={`${a}-${i}`} className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-3.5 py-1.5 text-sm text-navy-900">
                        <CheckIcon className="h-4 w-4 shrink-0 text-emerald-600" />
                        {a}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="mt-5 text-sm italic text-gray-500">{t('detail.noAccessories')}</p>
              )}
            </section>
          </div>

          {/* Operación */}
          {(verticalId || producto.diff || producto.fichaUrl) && (
            <aside className="lg:sticky lg:top-28 lg:self-start">
              {(verticalId || producto.diff) && (
                <>
                  <h2 className="text-2xl font-bold text-navy-900">{t('detail.fitTitle')}</h2>
                  <div className="mt-2 h-1 w-12 rounded-full bg-neon-300" />
                  <div className={`mt-6 rounded-2xl border border-l-4 border-gray-200 bg-white p-5 shadow-sm ${levelStyle[producto.level]?.accent || 'border-l-electric-500'}`}>
                    {producto.level && (
                      <span className={`inline-block rounded-full px-3 py-1 text-[11px] font-bold tracking-wide ${levelStyle[producto.level].badge}`}>
                        {t(`pro.levels.${producto.level}.tag`)} · {t(`pro.levels.${producto.level}.name`)}
                      </span>
                    )}
                    {verticalId && <p className="mt-3 font-bold text-navy-900">{t(`pro.verticals.${verticalId}.title`)}</p>}
                    {producto.diff && <p className="mt-3 rounded-r-md border-l-[3px] border-electric-500 bg-electric-50/60 px-3 py-2 text-sm text-navy-800">{producto.diff}</p>}
                  </div>
                </>
              )}
              {producto.fichaUrl && (
                <a
                  href={producto.fichaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-electric-600 transition hover:text-navy-900"
                >
                  {t('detail.datasheet')} <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                </a>
              )}
            </aside>
          )}
        </div>

        {/* Banner SWRYA */}
        <section className="relative isolate mt-20 overflow-hidden rounded-3xl bg-navy-950 md:flex">
          <div className="relative min-h-[200px] bg-cover bg-center md:w-2/5" style={{ backgroundImage: `url(${swryaBanner.media})` }} aria-hidden="true">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-navy-950 max-md:bg-gradient-to-b" />
          </div>
          <div className="flex flex-1 flex-col justify-center gap-4 p-8 sm:p-10">
            <div className="flex flex-wrap items-center gap-3">
              <img src={swryaLogo} alt="SWRYA" className="h-7 w-auto rounded bg-white px-2 py-0.5" />
              <span className="rounded-full border border-white/25 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white/70">{swryaBanner.kicker}</span>
            </div>
            <h3 className="text-2xl font-bold text-white">{promo.title}</h3>
            <p className="max-w-xl text-sm leading-relaxed text-white/70">{promo.desc}</p>
            <div className="flex flex-wrap items-center gap-4">
              <span className="rounded-full bg-gradient-to-r from-orange-600 to-orange-500 px-4 py-1.5 text-xs font-bold text-white">{swryaBanner.tag}</span>
              <Link to="/swrya" className="text-sm font-bold text-neon-300 transition hover:text-white">
                {t('detail.learnMore')} <span aria-hidden="true">&rarr;</span>
              </Link>
            </div>
          </div>
        </section>

        {/* Recomendados */}
        {recomendados.length > 0 && (
          <section className="mt-20">
            <h2 className="text-2xl font-bold text-navy-900">{t('detail.recommended')}</h2>
            <div className="mt-2 h-1 w-12 rounded-full bg-neon-300" />
            <p className="mt-4 text-sm text-gray-500">{t('detail.recommendedSub')}</p>
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              {recomendados.map((r) => {
                const rImagen = r.specs?.find(([key]) => key === 'Image')?.[1]
                return (
                <Link
                  key={r.id}
                  to={`/catalogo/${r.id}`}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition duration-200 hover:-translate-y-1 hover:shadow-lg"
                >
                  <div className="flex h-28 items-center justify-center border-b border-gray-100 bg-electric-50/40 p-3">
                    {rImagen ? (
                      <img src={rImagen} alt={r.nombre} loading="lazy" className="max-h-24 object-contain" />
                    ) : (
                      <ImagePlaceholder className="h-full w-full rounded-lg" />
                    )}
                  </div>
                  <div className="p-3">
                    <span className="block text-[10.5px] uppercase tracking-wide text-gray-500">{r.marca}</span>
                    <span className="mt-0.5 block text-sm font-bold text-navy-900 group-hover:text-electric-600">{r.nombre}</span>
                  </div>
                </Link>
                )
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  )
}
